import Typography from '@mui/material/Typography'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@mui/styles'
import { Box, Chip, Grid } from '@mui/material'
import CustomTooltip from '@app/components/ui/CustomTooltip'

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1.2,
  },
  values: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.2,
  },
}))

interface Props {
  gameOptions?: GameOptions
  gameFormatId: number
  sideGameEnabled?: boolean
}

const GameOptions = ({ gameOptions, gameFormatId, sideGameEnabled }: Props) => {
  const classes = useStyles()

  if (!gameOptions) {
    return null
  }
  const primaryTieBreakMethod = gameOptions.primaryTieBreakMethod ?? undefined

  return (
    <Grid container sx={{ gap: 1 }}>
      <Grid container item sx={{ gap: 1 }}>
        <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
          <Typography variant={'body1'} className={classes.title}>
            <FormattedMessage id={'strings.gameFormat'} />:
          </Typography>
        </Grid>
        <Grid item lg={'auto'}>
          <Typography variant={'body1'} className={classes.values}>
            <FormattedMessage id={`gameFormats.type${gameFormatId}`} />
          </Typography>
        </Grid>
      </Grid>
      {gameOptions.countBestResults && (
        <Grid container sx={{ gap: 1 }}>
          <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
            <Typography variant={'body1'} className={classes.title}>
              <FormattedMessage id={'strings.scoresCounted'} />:
            </Typography>
          </Grid>
          <Grid item lg={'auto'}>
            <Typography variant={'body1'} className={classes.values}>
              <b>
                <FormattedMessage id={`gameFormats.countBestResults_${gameOptions.countBestResults}`} />
              </b>
            </Typography>
          </Grid>
        </Grid>
      )}
      {!!gameOptions.modifier && gameOptions.modifier !== 'regular' && (
        <Grid container sx={{ gap: 1 }}>
          <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
            <Typography variant={'body1'} className={classes.title}>
              <FormattedMessage id={'strings.modifier'} />:
            </Typography>
          </Grid>
          <Grid item lg={'auto'}>
            <Typography variant={'body1'} className={classes.values}>
              <b style={{ textTransform: 'capitalize' }}>{gameOptions.modifier.replaceAll('_', ' ')}</b>
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container sx={{ gap: 1 }}>
        <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
          <Typography variant={'body1'} className={classes.title}>
            <FormattedMessage id={'strings.useHandicaps'} />:
          </Typography>
        </Grid>
        <Grid item lg={'auto'}>
          <Typography variant={'body1'} className={classes.values}>
            <b>
              {gameOptions.useHandicaps ? <FormattedMessage id="buttons.yes" /> : <FormattedMessage id="buttons.no" />}
            </b>
          </Typography>
        </Grid>
      </Grid>
      {gameOptions.useHandicaps && (
        <Grid container sx={{ gap: 1 }}>
          <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
            <Typography variant={'body1'} className={classes.title}>
              <FormattedMessage id={'strings.handicapPercentage'} />:
            </Typography>
          </Grid>
          <Grid item lg={'auto'}>
            {gameOptions.usePerPlayerHandicapPercentages ? (
              <CustomTooltip
                slotProps={{
                  tooltip: {
                    sx: {
                      color: '#FFFFFF',
                      backgroundColor: '#222A35',
                    },
                  },
                }}
                title={
                  <>
                    <FormattedMessage id={'strings.perPlayerHCPPercentageTooltip.title'} />
                    <br />
                    <Box>
                      <FormattedMessage id={'strings.perPlayerHCPPercentageTooltip.lowestHCP'} />
                      {gameOptions.perPlayerHandicapPercentages.map((percentage, index) => (
                        <Chip
                          key={index}
                          label={<b>{percentage}%</b>}
                          size="small"
                          sx={{ backgroundColor: '#FFFFFF1A', color: '#BBBBBD', margin: '2px' }}
                        />
                      ))}
                      <FormattedMessage id={'strings.perPlayerHCPPercentageTooltip.highestHCP'} />
                    </Box>
                  </>
                }
              >
                <Typography variant={'body1'} className={classes.values} sx={{ textDecoration: 'underline' }}>
                  <b>{gameOptions.perPlayerHandicapPercentages.join('/')}</b>
                </Typography>
              </CustomTooltip>
            ) : (
              <Typography variant={'body1'} className={classes.values}>
                <b>{gameOptions.handicapPercentage}</b>
              </Typography>
            )}
          </Grid>
        </Grid>
      )}
      {gameOptions.limitToLowestPhcp && (
        <Grid container sx={{ gap: 1 }}>
          <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
            <Typography variant={'body1'} className={classes.title}>
              <FormattedMessage id={'strings.teamHcpLimit'} />:
            </Typography>
          </Grid>
          <Grid item lg={'auto'}>
            <Typography variant={'body1'} className={classes.values}>
              <FormattedMessage id={'strings.lowestIndividualPlayingHCP'} />
            </Typography>
          </Grid>
        </Grid>
      )}

      {primaryTieBreakMethod && (
        <Grid container sx={{ gap: 1 }}>
          <Grid item xs={12} sm={'auto'} lg={sideGameEnabled ? 5 : 3}>
            <Typography variant={'body1'}>
              <FormattedMessage id={'strings.tieBreakMethod'} />:
            </Typography>
          </Grid>
          <Grid item lg={'auto'}>
            <Typography variant={'body1'}>
              <b>
                <FormattedMessage id={`strings.tieBreakMethod_${primaryTieBreakMethod}`} />
              </b>
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}

export default GameOptions
